/**
 * 用途：广告平台账号管理页，维护各平台（Facebook / Google / TikTok）的广告账号列表及关联信息
 * 路由：/platform-accounts
 */
import { useEffect, useState, useCallback } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import Pagination from '../components/Pagination';
import { MultiSelect } from '../components/MultiSelect';
import { confirmDialog } from '../store/useConfirmStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authAccountsApi } from '../api/authAccounts';
import type { PlatformAccountSimple, PlatformAuthAccount } from '../api/authAccounts';
import { useAppStore } from '../store/useAppStore';
import { useToastStore } from '../store/useToastStore';
import { api } from '../api/client';
import type { BrandItem, SiteItem } from '../store/useAppStore';
import { TableLoadingCell, TableLoadingOverlay } from '../components/TableLoadingOverlay';

type PlatformFilter = 'all' | 'facebook' | 'google' | 'tiktok';

const DEFAULT_PAGE_SIZE = 10;
const PLATFORM_OPTIONS = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'google', label: 'Google' },
  { value: 'tiktok', label: 'TikTok' },
];
const STATUS_OPTIONS = [
  { value: 'active', label: '已启用' },
  { value: 'inactive', label: '未启用' },
  { value: 'expired', label: '已过期' },
  { value: 'error', label: '异常' },
];
const modalSelectStyle: React.CSSProperties = { width: '100%' };

function getStatusLabel(status: string): string {
  if (status === '1') return '已启用';
  if (status === '0') return '未启用';
  switch (status) {
    case 'active': return '已启用';
    case 'inactive': return '未启用';
    case 'expired': return '已过期';
    case 'error': return '异常';
    default: return status;
  }
}

function statusClass(status: string): string {
  const normalized = status === '1' ? 'active' : status === '0' ? 'inactive' : status;
  switch (normalized) {
    case 'active': return 'status-active';
    case 'expired':
    case 'error': return 'status-error';
    default: return 'status-paused';
  }
}

export default function PlatformAccounts() {
  const queryClient = useQueryClient();
  const { brandId, siteId, adContext } = useAppStore();
  const userLevel = adContext?.data_level ?? 1;
  const showToast = useToastStore((s) => s.show);
  const isMobile = useIsMobile();

  const [platformFilter, setPlatformFilter] = useState<PlatformFilter>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [showForm, setShowForm] = useState(false);

  // Form state (create)
  const [formUserID, setFormUserID] = useState('');
  const [formBrandID, setFormBrandID] = useState('');
  const [formSiteID, setFormSiteID] = useState('');
  const [formPlatform, setFormPlatform] = useState('');
  const [formAuthAccountID, setFormAuthAccountID] = useState('');

  // Form brands/sites (independent from global store, for the modal)
  const [formBrands, setFormBrands] = useState<BrandItem[]>([]);
  const [formSites, setFormSites] = useState<SiteItem[]>([]);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editUserID, setEditUserID] = useState('');
  const [editBrandID, setEditBrandID] = useState('');
  const [editSiteID, setEditSiteID] = useState('');
  const [editPlatform, setEditPlatform] = useState('');
  const [editAuthAccountID, setEditAuthAccountID] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editBrands, setEditBrands] = useState<BrandItem[]>([]);
  const [editSites, setEditSites] = useState<SiteItem[]>([]);

  // Fetch brands for modals
  const fetchBrandsList = useCallback(async () => {
    try {
      const res = await api.get<{ code: number; data: BrandItem[] }>('/brands');
      return res.data || [];
    } catch { return []; }
  }, []);

  const fetchSitesList = useCallback(async (bid: string) => {
    if (!bid) return [];
    try {
      const res = await api.get<{ code: number; data: SiteItem[] }>(`/brands/${bid}/sites`);
      return res.data || [];
    } catch { return []; }
  }, []);

  // Reset page when brand/site changes
  useEffect(() => {
    setCurrentPage(1);
  }, [brandId, siteId]);

  // List query — default filter by current brand + site
  const listQuery = useQuery({
    queryKey: ['platformAccounts', platformFilter, brandId, siteId, currentPage, pageSize],
    queryFn: () =>
      authAccountsApi.listPlatformAccounts({
        page: currentPage,
        size: pageSize,
        platform: platformFilter === 'all' ? undefined : platformFilter,
        brand_id: brandId || undefined,
        site_id: siteId || undefined,
        scope: 'platform_account_read',
      }),
    refetchInterval: 30000,
  });

  // Users dropdown — all active users under the brand
  const usersQuery = useQuery({
    queryKey: ['platformAccountUserOptions', brandId],
    queryFn: () => authAccountsApi.listUserOptions(brandId),
    enabled: !!brandId,
  });

  // Auth accounts for dropdown (filtered by platform) — create form
  const authAccountsQuery = useQuery({
    queryKey: ['authAccountsForSelect', formPlatform],
    queryFn: () =>
      authAccountsApi.list({
        platform: formPlatform as 'facebook' | 'google' | 'tiktok' | undefined,
        size: 100,
      }),
    enabled: !!formPlatform,
  });

  // Auth accounts for dropdown — edit form
  const editAuthAccountsQuery = useQuery({
    queryKey: ['authAccountsForSelect', editPlatform],
    queryFn: () =>
      authAccountsApi.list({
        platform: editPlatform as 'facebook' | 'google' | 'tiktok' | undefined,
        size: 100,
      }),
    enabled: !!editPlatform,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      authAccountsApi.createPlatformAccount({
        user_id: formUserID,
        brand_id: formBrandID,
        site_id: formSiteID,
        platform: formPlatform,
        auth_account_id: formAuthAccountID,
      }),
    onSuccess: () => {
      showToast('创建成功');
      setShowForm(false);
      resetForm();
      void queryClient.invalidateQueries({ queryKey: ['platformAccounts'] });
    },
    onError: (err: Error) => {
      showToast(err.message || '创建失败');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }: { id: string; user_id: string; brand_id: string; site_id: string; auth_account_id: string; status: string }) =>
      authAccountsApi.updatePlatformAccount(id, data),
    onSuccess: () => {
      showToast('更新成功');
      setEditingId(null);
      void queryClient.invalidateQueries({ queryKey: ['platformAccounts'] });
    },
    onError: (err: Error) => {
      showToast(err.message || '更新失败');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => authAccountsApi.deletePlatformAccount(id),
    onSuccess: () => {
      showToast('删除成功');
      void queryClient.invalidateQueries({ queryKey: ['platformAccounts'] });
    },
    onError: (err: Error) => {
      showToast(err.message || '删除失败');
    },
  });

  function resetForm() {
    setFormUserID('');
    setFormBrandID('');
    setFormSiteID('');
    setFormPlatform('');
    setFormAuthAccountID('');
    setFormSites([]);
  }

  // Load brands when create modal opens + auto-set platform from tab
  useEffect(() => {
    if (showForm) {
      void fetchBrandsList().then(setFormBrands);
      if (platformFilter !== 'all') {
        setFormPlatform(platformFilter);
      }
    }
  }, [showForm, fetchBrandsList, platformFilter]);

  // Load sites when brand changes in create form
  useEffect(() => {
    setFormSiteID('');
    if (formBrandID) {
      void fetchSitesList(formBrandID).then(setFormSites);
    } else {
      setFormSites([]);
    }
  }, [formBrandID, fetchSitesList]);

  // Reset auth account when platform changes
  useEffect(() => {
    setFormAuthAccountID('');
  }, [formPlatform]);

  // Load brands when edit modal opens
  useEffect(() => {
    if (editingId) {
      void fetchBrandsList().then(setEditBrands);
    }
  }, [editingId, fetchBrandsList]);

  // Load sites when brand changes in edit form
  useEffect(() => {
    if (editBrandID) {
      void fetchSitesList(editBrandID).then(setEditSites);
    } else {
      setEditSites([]);
    }
  }, [editBrandID, fetchSitesList]);

  const items = listQuery.data?.data ?? [];
  const total = listQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const users = usersQuery.data?.list ?? [];
  const authAccounts: PlatformAuthAccount[] = authAccountsQuery.data?.data ?? [];
  const editAuthAccounts: PlatformAuthAccount[] = editAuthAccountsQuery.data?.data ?? [];

  const canSubmit = formUserID && formBrandID && formSiteID && formPlatform && formAuthAccountID;
  const canEdit = editUserID && editBrandID && editSiteID && editAuthAccountID && editStatus;
  const showLeaderPermissionToast = () => showToast('需主管及以上权限操作，如需使用此功能请申请权限');

  const handleEdit = (a: PlatformAccountSimple) => {
    if (userLevel < 3) {
      showLeaderPermissionToast();
      return;
    }
    setEditingId(a.id);
    setEditUserID(a.user_id || '');
    setEditBrandID(a.brand_id || '');
    setEditSiteID(a.site_id || '');
    setEditPlatform(a.platform || '');
    setEditAuthAccountID(a.auth_account_id || '');
    setEditStatus(a.status || 'active');
  };

  const colCount = 8;

  return (
    <div className="page-section" style={{ display: 'block' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: isMobile ? 12 : 0 }}>
        <div>
          <div className="section-title">平台账号管理</div>
          <div className="section-desc" style={{ marginTop: 4 }}>
            将授权账号分配给用户和站点，管理平台账号绑定关系。
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => {
          if (userLevel < 3) {
            showLeaderPermissionToast();
            return;
          }
          setShowForm(true);
        }}>
          新建平台账号
        </button>
      </div>

      <div className="toolbar">
        <div className="page-tabs">
          {(['all', 'facebook', 'google', 'tiktok'] as PlatformFilter[]).map((f) => (
            <div key={f} className={`page-tab ${platformFilter === f ? 'active' : ''}`}
              onClick={() => { setPlatformFilter(f); setCurrentPage(1); }}>
              {f === 'all' ? '全部' : f === 'facebook' ? 'Facebook' : f === 'google' ? 'Google' : 'TikTok'}
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ padding: 0, marginTop: 16 }}>
        <div className="table-wrap" style={{ position: 'relative' }}>
          <table style={{ minWidth: 700 }}>
            <thead>
              <tr>
                <th style={{ whiteSpace: 'nowrap' }}>用户名</th>
                <th style={{ whiteSpace: 'nowrap' }}>品牌</th>
                <th style={{ whiteSpace: 'nowrap' }}>站点</th>
                <th style={{ whiteSpace: 'nowrap' }}>平台</th>
                <th style={{ whiteSpace: 'nowrap' }}>账号名称</th>
                <th style={{ whiteSpace: 'nowrap' }}>账号 ID</th>
                <th style={{ whiteSpace: 'nowrap' }}>状态</th>
                <th style={{ whiteSpace: 'nowrap' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {listQuery.isLoading && items.length === 0 && (
                <tr><td colSpan={colCount}><TableLoadingCell /></td></tr>
              )}
              {listQuery.isError && (
                <tr><td colSpan={colCount} style={{ padding: 24, color: 'var(--red)', textAlign: 'center' }}>
                  {(listQuery.error as Error).message || '加载失败'}
                </td></tr>
              )}
              {!listQuery.isLoading && !listQuery.isError && items.length === 0 && (
                <tr><td colSpan={colCount} style={{ padding: 24, color: 'var(--text3)', textAlign: 'center' }}>
                  暂无平台账号，点击右上角新建。
                </td></tr>
              )}
              {items.map((a: PlatformAccountSimple) => (
                <tr key={a.id}>
                  <td>{a.user_name || '-'}</td>
                  <td>{a.brand_name || '-'}</td>
                  <td>{a.site_name || '-'}</td>
                  <td style={{ textTransform: 'capitalize' }}>{a.platform}</td>
                  <td>{a.account_name || '-'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{a.account_id}</td>
                  <td><span className={`status ${statusClass(a.status)}`}>{getStatusLabel(a.status)}</span></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => handleEdit(a)}>
                      编辑
                    </button>
                    <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }}
                      onClick={async () => {
                        if (userLevel < 3) { showLeaderPermissionToast(); return; }
                        const ok = await confirmDialog({
                          title: '确认删除',
                          message: `确定要删除平台账号 ${a.account_name || a.account_id} 吗？`,
                        });
                        if (ok) deleteMutation.mutate(a.id);
                      }}>
                      删除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {listQuery.isFetching && items.length > 0 && <TableLoadingOverlay />}
        </div>

        <Pagination
          page={currentPage}
          totalPages={totalPages}
          onChange={(p) => setCurrentPage(p)}
          total={`共 ${total} 条`}
          pageSize={pageSize}
          pageSizeOptions={[10, 20, 50, 100]}
          onPageSizeChange={nextPageSize => { setCurrentPage(1); setPageSize(nextPageSize); }}
          disabled={listQuery.isFetching}
        />
      </div>

      {/* Create Form Modal */}
      {showForm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9998,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setShowForm(false)}>
          <div className="card" style={{ width: 480, maxWidth: '90vw', padding: 24 }}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: 16 }}>新建平台账号</h3>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>选择用户</label>
              <MultiSelect
                single
                searchable
                options={users.map((u) => ({ value: u.user_id, label: u.user_name }))}
                value={formUserID ? [formUserID] : []}
                onChange={(values) => setFormUserID(values[0] ?? '')}
                placeholder="请选择用户"
                style={modalSelectStyle}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>选择品牌</label>
              <MultiSelect
                single
                searchable
                options={formBrands.map((b) => ({ value: String(b.id), label: b.name }))}
                value={formBrandID ? [formBrandID] : []}
                onChange={(values) => setFormBrandID(values[0] ?? '')}
                placeholder="请选择品牌"
                style={modalSelectStyle}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>选择站点</label>
              <MultiSelect
                single
                searchable
                options={formSites.map((s) => ({ value: String(s.id), label: `${s.name} (${s.code})` }))}
                value={formSiteID ? [formSiteID] : []}
                onChange={(values) => setFormSiteID(values[0] ?? '')}
                placeholder={formBrandID ? '请选择站点' : '请先选择品牌'}
                disabled={!formBrandID}
                style={modalSelectStyle}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>选择平台</label>
              <MultiSelect
                single
                options={PLATFORM_OPTIONS}
                value={formPlatform ? [formPlatform] : []}
                onChange={(values) => setFormPlatform(values[0] ?? '')}
                placeholder="请选择平台"
                disabled={platformFilter !== 'all'}
                style={modalSelectStyle}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>选择授权账号</label>
              <MultiSelect
                single
                searchable
                options={authAccounts.map((a) => ({ value: String(a.id), label: `${a.account_name || a.account_id} (${a.account_id})` }))}
                value={formAuthAccountID ? [formAuthAccountID] : []}
                onChange={(values) => setFormAuthAccountID(values[0] ?? '')}
                placeholder={formPlatform ? '请选择授权账号' : '请先选择平台'}
                disabled={!formPlatform}
                style={modalSelectStyle}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-ghost" onClick={() => { setShowForm(false); resetForm(); }}>
                取消
              </button>
              <button className="btn btn-primary" disabled={!canSubmit || createMutation.isPending || userLevel < 3}
                onClick={() => { if (userLevel < 3) { showLeaderPermissionToast(); return; } createMutation.mutate(); }}>
                {createMutation.isPending ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Form Modal */}
      {editingId && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9998,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setEditingId(null)}>
          <div className="card" style={{ width: 480, maxWidth: '90vw', padding: 24 }}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: 16 }}>编辑平台账号</h3>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>选择用户</label>
              <MultiSelect
                single
                searchable
                options={users.map((u) => ({ value: u.user_id, label: u.user_name }))}
                value={editUserID ? [editUserID] : []}
                onChange={(values) => setEditUserID(values[0] ?? '')}
                placeholder="请选择用户"
                style={modalSelectStyle}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>选择品牌</label>
              <MultiSelect
                single
                searchable
                options={editBrands.map((b) => ({ value: String(b.id), label: b.name }))}
                value={editBrandID ? [editBrandID] : []}
                onChange={(values) => {
                  setEditBrandID(values[0] ?? '');
                  setEditSiteID('');
                }}
                placeholder="请选择品牌"
                style={modalSelectStyle}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>选择站点</label>
              <MultiSelect
                single
                searchable
                options={editSites.map((s) => ({ value: String(s.id), label: `${s.name} (${s.code})` }))}
                value={editSiteID ? [editSiteID] : []}
                onChange={(values) => setEditSiteID(values[0] ?? '')}
                placeholder={editBrandID ? '请选择站点' : '请先选择品牌'}
                disabled={!editBrandID}
                style={modalSelectStyle}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>平台</label>
              <MultiSelect
                single
                options={PLATFORM_OPTIONS}
                value={editPlatform ? [editPlatform] : []}
                onChange={() => undefined}
                disabled
                style={modalSelectStyle}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>选择授权账号</label>
              <MultiSelect
                single
                searchable
                options={editAuthAccounts.map((a) => ({ value: String(a.id), label: `${a.account_name || a.account_id} (${a.account_id})` }))}
                value={editAuthAccountID ? [editAuthAccountID] : []}
                onChange={(values) => setEditAuthAccountID(values[0] ?? '')}
                placeholder="请选择授权账号"
                style={modalSelectStyle}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>状态</label>
              <MultiSelect
                single
                options={STATUS_OPTIONS}
                value={editStatus ? [editStatus] : []}
                onChange={(values) => setEditStatus(values[0] ?? '')}
                placeholder="请选择状态"
                style={modalSelectStyle}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-ghost" onClick={() => setEditingId(null)}>
                取消
              </button>
              <button className="btn btn-primary" disabled={!canEdit || updateMutation.isPending || userLevel < 3}
                onClick={() => {
                  if (userLevel < 3) { showLeaderPermissionToast(); return; }
                  updateMutation.mutate({
                    id: editingId,
                    user_id: editUserID,
                    brand_id: editBrandID,
                    site_id: editSiteID,
                    auth_account_id: editAuthAccountID,
                    status: editStatus,
                  });
                }}>
                {updateMutation.isPending ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
